import React, { useEffect, CSSProperties } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import SwipeableViews from 'react-swipeable-views';
import { CGProduct } from '../../types/shopify';
import ProductHeroCard from './ProductHeroCard';
import Indicators from './Indicators';
import { productGetSplash } from '../../lib/product';
import SplitPageTitle from 'components/Layout/SplitPageTitle';
import Box from '@mui/material/Box';
import clxs from 'classnames';
import has from 'lodash/has';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { cgtag } from 'utilities/gtag';
import { shopifyExtractId } from 'lib/shopifyHelpers';

interface ProductCarouselProps {
  products: CGProduct[];
  title?: string;
  description?: string;
  backgroundImage?: string;
  mode?: 'selectCard' | 'viewCard' | 'viewCardSimple';
  noPadding?: boolean;
  cardValue?: number;
  buttonLabel?: string;
  style?: CSSProperties;
  source?: string;
  hasBlurBackground?: boolean;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    homeStyle: {
      position: 'relative',
      height: '100vh',
      minHeight: 800,
    },
    notHomeStyle: {
      position: 'relative',
      height: 'calc(100vh - 60px)',
      minHeight: 800,
    },
    root: {
      userSelect: 'none',
      '& *': {
        userSelect: 'none',
      },
      position: 'relative',
      marginBottom: theme.spacing(6),
    },
    viewCard: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 0,
      minHeight: 800,
      height: '100%',
    },
    selectCard: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 800,
      marginBottom: 0,
      height: '100%',
    },
    bottomContent: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      padding: '0 calc(25% - 108px)',
      flexGrow: 1,
      [theme.breakpoints.down('lg')]: {
        padding: '0 calc(25% - 72px)',
      },
      [theme.breakpoints.down('md')]: {
        padding: '0 calc(25% - 53px)',
      },
    },
    splash: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      transition: 'all 0.5s ease-in-out',
      opacity: 0.5,
      '-webkit-mask-image': 'linear-gradient(rgba(0,0,0,1) 0 98%, rgba(0,0,0,0) 100%)',
    },
    titleContent: {
      position: 'absolute',
      top: theme.spacing(8),
      width: '100%',
      left: '50%',
      transform: 'translate(-50%, 0%)',
    },
    indicators: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      [theme.breakpoints.down('lg')]: {
        bottom: theme.spacing(4),
      },
    },
    marginTop: {
      marginTop: theme.spacing(30),
    },
    blur: {
      opacity: '0.5',
      filter: 'blur(6px)',
    },
  }),
  { name: 'ProductCarousel' },
);

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  mode = 'viewCard',
  products,
  title,
  backgroundImage,
  description,
  noPadding = false,
  cardValue,
  buttonLabel,
  style,
  source,
  hasBlurBackground = false,
}) => {
  const classes = useStyles();
  const [backgroundUrl, setBackgroundUrl] = React.useState<string | undefined>();
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const onChange = (index: number) => {
    setCurrentSlide(index);
    const Product = products[index];
    cgtag('carousel_card_view', {
      index,
      mode: mode,
      item_id: shopifyExtractId(Product.id.toString()),
      item_name: Product.title,
    });
    const productSplash = productGetSplash(Product);
    if (productSplash) {
      setBackgroundUrl(productSplash);
    } else if (products[index].backgroundImage) {
      setBackgroundUrl(products[index].backgroundImage);
    } else {
      setBackgroundUrl(backgroundImage);
    }
  };

  useEffect(() => {
    if (products && products.length && products[0]) {
      cgtag('carousel_card_view', {
        index: 0,
        mode: mode,
        item_id: shopifyExtractId(products[0].id.toString()),
        item_name: products[0].title,
      });
    }
    // TODO - Stop the product page re-rendering so many times, so this can be based on products
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (products.length && products[0]) {
      const productSplash = productGetSplash(products[0]);
      if (productSplash) {
        setBackgroundUrl(productSplash);
      } else if (products[0].backgroundImage) {
        setBackgroundUrl(products[0].backgroundImage);
      } else {
        setBackgroundUrl(backgroundImage);
      }
    }
  }, [products, setBackgroundUrl, backgroundImage]);

  return (
    <div className={clxs(source === 'home' ? classes.homeStyle : classes.notHomeStyle)}>
      <div className={clxs(classes.root, (mode === 'viewCard' || mode === 'viewCardSimple') && classes.viewCard, mode === 'selectCard' && classes.selectCard)} style={style}>
        <div
          className={clxs(classes.splash, hasBlurBackground && classes.blur)}
          style={{
            backgroundImage: backgroundUrl ? 'url(' + backgroundUrl + ')' : has(products[currentSlide], 'backgroundImage') ? products[currentSlide].backgroundImage : undefined,
          }}
        />
        {source === 'home' && (
          <Stack
            sx={{
              width: '100%',
              flexGrow: 1,
              position: 'relative',
              mt: 30,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                zIndex: 1000,
                position: 'absolute',
                bottom: 36,
                left: {
                  sm: '50%',
                },
                transform: {
                  sm: 'translateX(-50%)',
                },
                width: '100%',
                maxWidth: '767px',
                textAlign: 'center',
                fontSize: {
                  xs: '30px',
                  sm: '38px',
                },
                lineHeight: {
                  xs: '33px',
                  sm: '41px',
                },
                px: 2,
              }}
            >
              {description || 'Send physical or digital gift cards, or create your own.'}
            </Typography>
          </Stack>
        )}
        <SwipeableViews enableMouseEvents index={currentSlide} hysteresis={0.1} onChangeIndex={onChange} className={clxs(classes.bottomContent, source !== 'home' && classes.marginTop)}>
          {products.map((Product: CGProduct, index: number) => (
            <ProductHeroCard
              key={Product.id}
              mode={mode}
              item={Product}
              activeIdx={currentSlide}
              idx={index}
              selected={index === currentSlide}
              click={onChange}
              noPadding={noPadding}
              cardValue={cardValue}
              viewButtonLabel={buttonLabel}
            />
          ))}
        </SwipeableViews>
        <Indicators active={currentSlide} press={onChange} length={products.length} className={classes.indicators} />
        {(title || description) && source !== 'home' && (
          <Box className={classes.titleContent}>
            <SplitPageTitle
              title={title || ''}
              subtitle={description}
              subtitleStyle={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel;
